import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { createGlobalState } from '@vueuse/core';
import type { HistoryRowData, NavMenuItem } from '../types';
import { getHistoryList } from '../infrastructure/api-functions';
import { create } from '@eureka/utils/deferred';
import { getHistoryRouterData } from '../infrastructure/utils/history-router';
import { flatMap } from 'lodash-es';
import { formatDateToLocal } from '@eureka/utils/date-format';
import { getCurrentLanguage } from '@eureka/language-tool';

export const useNavBarState = createGlobalState(
    () => {
        const opened = ref(false)

        const loading = ref(true);

        const route = useRoute();

        const currentLang = computed(() => getCurrentLanguage() as 'cn' | 'en' | 'jp' | 'tw');

        const listDeferred = create<NavMenuItem[]>();

        const historyList: Ref<NavMenuItem[]> = ref([]);

        const currentDetailId = computed(() => {
            return route.params?.seek_id || route.params?.board_id || route.params?.projectId || route.params?.fmea_id || route.query?.board_id || route.params?.taskId || route.params?.id || route.params?.agent_job_id || route.params?.lsAgentId || route.params?.graph_id || route.params?.triz_mind_id || route.params?.conversation_id
        })

        const sessionsTaskData = ref<any[]>([]);

        function updateHistory(list: NavMenuItem[]) {
            historyList.value = list;
        }

        function queryList() {
            loading.value = true;
            getHistoryList().then((res) => {
                const data = flatMap(res.history_maps, 'data') as HistoryRowData[]
                historyList.value = data.map(item => {
                    const { router, module } = getHistoryRouterData(item.source, item.id);
                    return {
                        type: item.source,
                        id: item.id,
                        title: item.message,
                        link: item.link,
                        router,
                        module,
                        icon_url: item.icon_url,
                        business_name: item.business_name,
                        active: item.id === currentDetailId.value,
                    }
                })
                listDeferred.resolve(historyList.value)
                loading.value = false;
            });
        }

        watch(currentDetailId, () => {
            historyList.value = historyList.value.map(item => ({ ...item, active: item.id === currentDetailId.value }))
        })

        async function getSessionsTaskData(query: string = '') {
            const res = await getHistoryList(query);
            sessionsTaskData.value = flatMap(res.history_maps, 'data').map((item: HistoryRowData) => {
                return {
                    ...item,
                    created_at: formatDateToLocal({ date: item.created_at, currentLang: currentLang.value }),
                }
            });
        }

        return {
            opened,
            loading,
            historyList,
            currentDetailId,
            queryList,
            updateHistory,
            getSessionsTaskData,
            sessionsTaskData,
        }
    }
)
